import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Heart, CreditCard, Smartphone, DollarSign } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";

import { API } from "@/config";

export default function DonatePage() {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [tiers, setTiers] = useState([]);
  const [mobileMoney, setMobileMoney] = useState({ mtn_number: "", airtel_number: "", registered_name: "" });
  const [paypalLink, setPaypalLink] = useState("https://paypal.me");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API}/donations/packages`);
        if (data.packages) {
          const tierList = Object.entries(data.packages).map(([id, pkg]) => ({
            id, name: pkg.name, amount: pkg.amount, description: pkg.description,
          }));
          setTiers(tierList);
        }
        if (data.mobile_money) setMobileMoney(data.mobile_money);
        if (data.paypal_link) setPaypalLink(data.paypal_link);
      } catch (err) {
        console.error("Failed to load donation settings:", err.message);
      }
    };
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch on mount; API is a module constant

  const handleStripeDonation = async (packageId) => {
    setLoading(packageId);
    setError("");
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/donations/checkout`, {
        package_id: packageId,
        origin_url: originUrl,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create checkout session");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div data-testid="donate-page">
      {/* Hero */}
      <section className="bg-[#C4392D] py-20 md:py-28">
        <div className="section-container text-center">
          <Heart size={40} className="text-white/80 mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Support Our Ministry
          </h1>
          <p className="text-white/80 text-base md:text-lg mt-4 max-w-xl mx-auto">
            Your generous donation helps us continue serving through music and community outreach.
          </p>
        </div>
      </section>

      {/* Donation Options */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="section-container max-w-4xl mx-auto">
          <Tabs defaultValue="stripe" className="w-full" data-testid="donate-tabs">
            <TabsList className="grid w-full grid-cols-3 mb-6 sm:mb-8 bg-white border border-gray-100 rounded-2xl p-1 h-auto">
              <TabsTrigger value="stripe" className="flex items-center justify-center gap-1.5 py-3 rounded-xl text-xs sm:text-sm data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="tab-stripe">
                <CreditCard size={14} /> <span className="hidden sm:inline">Card</span><span className="sm:hidden">Card</span>
              </TabsTrigger>
              <TabsTrigger value="mobile" className="flex items-center justify-center gap-1.5 py-3 rounded-xl text-xs sm:text-sm data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="tab-mobile">
                <Smartphone size={14} /> <span className="hidden sm:inline">Mobile Money</span><span className="sm:hidden">Mobile</span>
              </TabsTrigger>
              <TabsTrigger value="paypal" className="flex items-center justify-center gap-1.5 py-3 rounded-xl text-xs sm:text-sm data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="tab-paypal">
                <DollarSign size={14} /> PayPal
              </TabsTrigger>
            </TabsList>

            {/* Stripe Tab */}
            <TabsContent value="stripe" data-testid="stripe-content">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Choose a Donation Amount
                </h2>
                <p className="text-[#4A4A4A] text-sm mt-2">Secure payment powered by Stripe</p>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm" data-testid="donate-error">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tiers.map((tier) => (
                  <div
                    key={tier.id}
                    className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all rounded-2xl p-6 text-center"
                    data-testid={`donation-tier-${tier.id}`}
                  >
                    <p className="text-[#E8922F] text-sm font-semibold uppercase tracking-wider mb-2">{tier.name}</p>
                    <p className="text-4xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      ${tier.amount}
                    </p>
                    <p className="text-[#4A4A4A] text-sm mb-4">{tier.description}</p>
                    <Button
                      className="btn-primary w-full"
                      onClick={() => handleStripeDonation(tier.id)}
                      disabled={loading === tier.id}
                      data-testid={`donate-btn-${tier.id}`}
                    >
                      {loading === tier.id ? "Processing..." : `Donate $${tier.amount}`}
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Mobile Money Tab */}
            <TabsContent value="mobile" data-testid="mobile-content">
              <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
                <Smartphone size={48} className="text-[#E8922F] mx-auto mb-4" />
                <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Mobile Money Donation
                </h2>
                <p className="text-[#4A4A4A] text-sm mb-6 max-w-md mx-auto">
                  You can send your donation via Mobile Money using the details below:
                </p>
                <div className="bg-[#FFF3E6] rounded-2xl p-6 max-w-sm mx-auto text-left space-y-3">
                  <div>
                    <p className="text-xs text-[#4A4A4A] uppercase tracking-wider">MTN Mobile Money</p>
                    <p className="text-[#1A1A1A] font-bold text-lg" data-testid="mtn-display">{mobileMoney.mtn_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4A4A4A] uppercase tracking-wider">Airtel Money</p>
                    <p className="text-[#1A1A1A] font-bold text-lg" data-testid="airtel-display">{mobileMoney.airtel_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4A4A4A] uppercase tracking-wider">Registered Name</p>
                    <p className="text-[#1A1A1A] font-bold" data-testid="mobile-name-display">{mobileMoney.registered_name}</p>
                  </div>
                </div>
                <p className="text-[#4A4A4A] text-xs mt-4 italic">
                  Please include "Choir Donation" in the reference/message.
                </p>
              </div>
            </TabsContent>

            {/* PayPal Tab */}
            <TabsContent value="paypal" data-testid="paypal-content">
              <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
                <DollarSign size={48} className="text-[#E8922F] mx-auto mb-4" />
                <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  PayPal Donation
                </h2>
                <p className="text-[#4A4A4A] text-sm mb-6 max-w-md mx-auto">
                  You can donate securely through PayPal by clicking the button below.
                </p>
                <a
                  href={paypalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="paypal-donate-link"
                >
                  <Button className="bg-[#0070ba] text-white hover:bg-[#005ea6] rounded-full px-10 py-4 font-semibold text-base transition-all">
                    Donate via PayPal
                  </Button>
                </a>
                <p className="text-[#4A4A4A] text-xs mt-4 italic">
                  You will be redirected to PayPal to complete your donation.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
